import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { MailX, MailCheck } from "lucide-react";

const COPY = {
  nts: {
    title: "NTS learner emails",
    intro: "Switch for every email sent to NTS students from the CRM. Retrofit (T4R) learners have their own switch below.",
    on: "ON — NTS students receive emails", off: "OFF — nothing is emailed to NTS students",
    covers: "Covers course booking emails, certificate expiry reminders, receipts and invoices sent to students. Emails to staff and to employers or other contacts still go out. Students opted in to emails still receive them.",
  },
  retrofit: {
    title: "Retrofit learner emails",
    intro: "Switch for every email sent to Retrofit (T4R) learners. NTS students have their own switch above. This switch is also at the top of T4R → Learners.",
    on: "ON — Retrofit learners receive emails", off: "OFF — nothing is emailed to Retrofit learners unless they're opted in",
    covers: "Covers Retrofit invites, reminders, enrolment, announcement and group emails. Learners with emails turned on for them still receive them.",
  },
};

export const useLearnerEmails = (portal = "nts") => {
  const [enabled, setEnabled] = useState(null);
  useEffect(() => { api.get("/settings/learner-emails").then(({ data }) => setEnabled(portal === "retrofit" ? data.retrofit_enabled : data.enabled)).catch(() => {}); }, [portal]);
  return [enabled, setEnabled];
};

export const LearnerEmailsSwitch = ({ canEdit, portal = "nts" }) => {
  const [enabled, setEnabled] = useLearnerEmails(portal);
  const [busy, setBusy] = useState(false);
  const c = COPY[portal];
  const toggle = async (v) => {
    setBusy(true);
    try { await api.put(`/settings/learner-emails?portal=${portal}`, { enabled: v }); setEnabled(v); toast.success(`${c.title} switched ${v ? "ON" : "OFF"}`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't update"); }
    finally { setBusy(false); }
  };
  if (enabled === null) return null;
  return (
    <div className="max-w-3xl space-y-4 rounded-xl border border-border bg-white p-6" data-testid={`learner-emails-settings-${portal}`}>
      <div className="flex items-start justify-between gap-6">
        <div>
          <h3 className="font-heading text-lg font-semibold">{c.title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{c.intro}</p>
        </div>
        <Switch checked={enabled} disabled={!canEdit || busy} onCheckedChange={toggle} data-testid={`learner-emails-switch-${portal}`} />
      </div>
      <div className={`flex items-start gap-3 rounded-lg p-3 text-sm ${enabled ? "bg-green-50 text-green-900" : "bg-amber-50 text-amber-900"}`} data-testid={`learner-emails-status-${portal}`}>
        {enabled ? <MailCheck size={18} className="mt-0.5 shrink-0" /> : <MailX size={18} className="mt-0.5 shrink-0" />}
        <div>
          <p className="font-semibold">{enabled ? c.on : c.off}</p>
          <p className="mt-0.5 text-xs">{c.covers}</p>
        </div>
      </div>
      {!canEdit && <p className="text-xs text-muted-foreground">Only super admins can change this.</p>}
    </div>
  );
};

// Compact Retrofit switch shown at the top of T4R → Learners.
export const RetrofitEmailsBar = ({ canEdit }) => {
  const [enabled, setEnabled] = useLearnerEmails("retrofit");
  const [busy, setBusy] = useState(false);
  if (enabled === null) return null;
  const toggle = async (v) => {
    setBusy(true);
    try { await api.put("/settings/learner-emails?portal=retrofit", { enabled: v }); setEnabled(v); toast.success(`Retrofit learner emails switched ${v ? "ON" : "OFF"}`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't update"); }
    finally { setBusy(false); }
  };
  return (
    <div className={`flex flex-wrap items-center gap-2 rounded-xl border px-4 py-2.5 text-sm ${enabled ? "border-green-200 bg-green-50 text-green-900" : "border-amber-200 bg-amber-50 text-amber-900"}`} data-testid="retrofit-emails-bar">
      {enabled ? <MailCheck size={16} /> : <MailX size={16} />}
      <span><strong>Retrofit learner emails are {enabled ? "on" : "off"}.</strong> {enabled ? "Invites, reminders and group emails are sent." : "Only learners opted in to emails are emailed. Use \"Copy invite link\" for the rest."}</span>
      <span className="ml-auto flex items-center gap-3 text-xs">
        <Link to="/settings?tab=learner-emails" className="text-muted-foreground underline">NTS emails are switched separately in Settings</Link>
        {canEdit ? <label className="flex items-center gap-1.5 font-semibold">Retrofit emails <Switch checked={enabled} disabled={busy} onCheckedChange={toggle} data-testid="retrofit-emails-switch" /></label> : <span className="text-muted-foreground">Only super admins can change this</span>}
      </span>
    </div>
  );
};
